// Media detail — /<slug>/media/<id>
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Trash2, MapPin, Clock, User, Pencil, Check, X as XIcon, Smartphone } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import CommentsSection from "../components/CommentsSection";
import { Media, Albums } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { getGuestId } from "../lib/guestId";
import { saveItemsToDevice, canWebShareFiles } from "../lib/download";

export default function MediaDetail() {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [album, setAlbum] = useState(null);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Albums.get(slug).then(setAlbum).catch(() => {});
    Media.list(slug).then((all) => {
      const found = all.find((m) => m.id === id);
      if (!found) {
        toast.error("Not found");
        navigate(`/${slug}/gallery`);
      } else {
        setItem(found);
        setTitleDraft(found.title || "");
        setDescDraft(found.description || "");
      }
    });
  }, [slug, id, navigate]);

  const onDelete = async () => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await Media.remove(id);
      toast.success("Deleted");
      navigate(`/${slug}/gallery`);
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const updated = await Media.patch(id, {
        title: titleDraft,
        description: descDraft,
      });
      setItem((prev) => ({ ...prev, title: updated.title, description: updated.description }));
      setEditing(false);
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  // True on phones/tablets that support sharing files to Photos / Camera Roll.
  // Web Share API w/ files → iOS shows "Save Image"/"Save Video to Photos",
  // Android shows "Save to Files" / Google Photos in the share sheet.
  const canSaveToCameraRoll = useMemo(() => canWebShareFiles(), []);
  const [savingDevice, setSavingDevice] = useState(false);

  if (!item) {
    return <PageShell><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  const isHost = !!(user && album && album.owner_id && album.owner_id === user.user_id);
  const isUploader = !!(item.guest_id && getGuestId() === item.guest_id);
  const canEdit = isHost;
  const canDelete = isHost || isUploader;

  const onSaveToDevice = async () => {
    if (savingDevice) return;
    setSavingDevice(true);
    const tid = toast.loading(
      canSaveToCameraRoll
        ? (item.is_video ? "Preparing video…" : "Preparing photo…")
        : "Downloading…"
    );
    try {
      const result = await saveItemsToDevice([item]);
      toast.dismiss(tid);
      if (result.ok) {
        if (result.mode === "share") {
          toast.success(item.is_video ? "Tap Save Video to Photos" : "Tap Save Image to Photos");
        } else {
          toast.success("Saved to your downloads");
        }
      } else if (result.reason === "cancelled") {
        // user dismissed share sheet — say nothing
      } else {
        toast.error("Save failed — try again");
      }
    } catch (e) {
      toast.dismiss(tid);
      toast.error("Save failed — try again");
    } finally {
      setSavingDevice(false);
    }
  };

  return (
    <PageShell
      headerLeft={
        <button onClick={() => navigate(`/${slug}/gallery`)} className="meye-chip" style={{ padding: "8px 12px" }} data-testid="back-to-gallery">
          <ArrowLeft size={14} /> Gallery
        </button>
      }
    >
      <div className="glass-card overflow-hidden mb-4" data-testid="media-viewer">
        {!item.is_video ? (
          <img src={Media.previewUrl(item.id)} alt={item.title || "Media"} style={{ width: "100%", display: "block" }} />
        ) : (
          <video
            // Streams from /preview which honours Range — iOS Safari won't play
            // anything from /download because of the Content-Disposition header.
            src={Media.previewUrl(item.id)}
            poster={Media.thumbUrl(item.id)}
            controls
            playsInline
            preload="metadata"
            style={{ width: "100%", display: "block", background: "#000", maxHeight: "70vh" }}
            data-testid="media-video"
          />
        )}
      </div>

      <div className="glass-card p-4 mb-4">
        {!editing ? (
          <>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                {item.title ? (
                  <div className="font-semibold text-lg leading-tight" data-testid="media-title">{item.title}</div>
                ) : (
                  <div className="text-sm" style={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic" }} data-testid="media-title-empty">
                    {canEdit ? "Add a title…" : "Untitled"}
                  </div>
                )}
              </div>
              {canEdit && (
                <button
                  className="p-1.5 rounded-lg"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
                  onClick={() => setEditing(true)}
                  title="Edit"
                  data-testid="edit-media-btn"
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
            {item.description && (
              <div className="text-sm whitespace-pre-wrap mt-1 mb-3" style={{ color: "rgba(255,255,255,0.78)" }} data-testid="media-description">
                {item.description}
              </div>
            )}
            <div className="flex flex-col gap-2 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
              <div className="flex items-center gap-2"><User size={14} /> from {item.uploader_name}</div>
              {item.created_at_exif && (
                <div className="flex items-center gap-2"><Clock size={14} /> {new Date(item.created_at_exif).toLocaleString()}</div>
              )}
              {item.location && (
                <div className="flex items-center gap-2">
                  <MapPin size={14} />
                  <a
                    target="_blank" rel="noreferrer"
                    href={`https://www.google.com/maps?q=${item.location.lat},${item.location.lng}`}
                    style={{ color: "#22E2FF" }}
                  >
                    {item.location.lat.toFixed(4)}, {item.location.lng.toFixed(4)}
                  </a>
                </div>
              )}
            </div>
          </>
        ) : (
          <div data-testid="edit-form">
            <label className="section-label block mb-2">TITLE</label>
            <input
              className="meye-input mb-3"
              placeholder="Add a title (optional)"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              maxLength={140}
              data-testid="edit-title-input"
            />
            <label className="section-label block mb-2">DESCRIPTION</label>
            <textarea
              className="meye-input mb-3"
              placeholder="Add a description (optional)"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              maxLength={1000}
              rows={3}
              style={{ resize: "vertical", minHeight: 80, fontFamily: "inherit" }}
              data-testid="edit-description-input"
            />
            <div className="flex gap-2">
              <button
                className="meye-chip flex-1 justify-center"
                onClick={() => { setEditing(false); setTitleDraft(item.title || ""); setDescDraft(item.description || ""); }}
                disabled={saving}
                data-testid="edit-cancel-btn"
              >
                <XIcon size={14} /> Cancel
              </button>
              <button
                className="cta-pill flex-1"
                onClick={onSave}
                disabled={saving}
                data-testid="edit-save-btn"
              >
                {saving ? <div className="meye-spinner" /> : <><Check size={14} /> Save</>}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={onSaveToDevice}
          disabled={savingDevice}
          className="meye-chip flex-1 justify-center"
          style={{
            background: "linear-gradient(135deg, rgba(127,75,255,0.16), rgba(34,226,255,0.16))",
            borderColor: "rgba(34,226,255,0.55)",
            color: "#fff",
            fontWeight: 600,
            padding: "12px 16px",
          }}
          data-testid="media-download-btn"
        >
          {savingDevice ? (
            <div className="meye-spinner" />
          ) : canSaveToCameraRoll ? (
            <>
              <Smartphone size={15} />
              <span>{item.is_video ? "Save video to camera roll" : "Save to camera roll"}</span>
            </>
          ) : (
            <>
              <Download size={15} />
              <span>{item.is_video ? "Download video" : "Download original"}</span>
            </>
          )}
        </button>
        {canDelete && (
          <button
            onClick={onDelete}
            className="meye-chip justify-center"
            style={{
              background: "rgba(255,107,107,0.10)",
              borderColor: "rgba(255,107,107,0.45)",
              color: "#FF6B6B",
              padding: "12px 14px",
            }}
            data-testid="media-delete-btn"
            title={isHost ? "Delete (host)" : "Delete (you uploaded this)"}
            aria-label="Delete"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <CommentsSection mediaId={item.id} canModerate={isHost} />
    </PageShell>
  );
}
